import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Snackbar,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import type { AdminItem } from '../adminSections';

// Placeholder content rendered inside an item accordion when the item is a
// scaffold or a Q2-pending panel. Shows the backing table, API endpoint, and
// fires a toast on any Add-button click so demo walkthroughs feel live.
interface AdminItemPlaceholderProps {
  item: AdminItem;
}

const AdminItemPlaceholder = ({ item }: AdminItemPlaceholderProps) => {
  const theme = useTheme();
  const [notice, setNotice] = useState<string | null>(null);
  const isPendingQ2 = item.mode === 'pending-q2';

  const onAddClick = () => {
    setNotice(
      isPendingQ2
        ? `${item.label} is pending a scope decision (see Q2 in the Open Questions doc).`
        : `Add ${item.label} — not yet wired (${item.sprintRef ?? 'pending sprint'}).`,
    );
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
        {item.tableName && (
          <Chip
            label={`Table: ${item.tableName}`}
            size="small"
            variant="outlined"
            color="primary"
          />
        )}
        {item.apiPath && (
          <Chip
            label={`API: ${item.apiPath}`}
            size="small"
            variant="outlined"
          />
        )}
        {item.sprintRef && !isPendingQ2 && (
          <Chip
            label={item.sprintRef}
            size="small"
            variant="outlined"
            color="warning"
          />
        )}
        {isPendingQ2 && (
          <Chip
            label="Pending Q2 scope decision"
            size="small"
            color="warning"
          />
        )}
      </Stack>

      {item.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {item.description}
        </Typography>
      )}

      {isPendingQ2 && item.pendingReason && (
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          {item.pendingReason}
        </Alert>
      )}

      <Paper
        variant="outlined"
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 2,
          backgroundColor:
            theme.palette.mode === 'light'
              ? theme.palette.grey[50]
              : theme.palette.background.default,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="subtitle2" color="text.secondary">
            Data grid preview
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={onAddClick}
            disabled={isPendingQ2}
          >
            Add {item.label}
          </Button>
        </Stack>

        <Divider />

        <Box
          sx={{
            minHeight: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
            border: `1px dashed ${theme.palette.divider}`,
            p: 3,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            align="center"
          >
            {isPendingQ2
              ? 'Panel rendering deferred until Q2 is resolved.'
              : `Full ${item.label} admin panel lands in ${item.sprintRef ?? 'a future sprint'}.`}
            <br />
            Until then this accordion is a navigation-only placeholder so the
            Admin tree can be demoed end-to-end.
          </Typography>
        </Box>
      </Paper>

      <Snackbar
        open={!!notice}
        autoHideDuration={3000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity={isPendingQ2 ? 'warning' : 'info'}
          variant="filled"
          onClose={() => setNotice(null)}
        >
          {notice}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminItemPlaceholder;
